<template>
    <div class="center-container-vertically">
        <form
            data-cy="email-address-form"
            onsubmit="return false"
            autocomplete="on"
        >
            <div class="input-container mb-2">
                <p>{{ t("default.alreadyLoggedInUser") }}</p>
            </div>
            <div class="input-container-center-contents mx-auto">
                <text-input
                    v-model="emailForm.emailAddress"
                    cypress-data="email-address-input"
                    container-style-class="medium-input-with-default-label"
                    input-style-class="medium-input"
                    label-style-class="medium-default-label"
                    label="labels.emailAddress"
                    placeholder="labels.empty"
                    name="email-address-input"
                    autocomplete-value="email"
                    input-type="email"
                    :is-disabled="true"
                />
                <div
                    class="error-container"
                    data-cy="email-address-error"
                    v-for="error in v$.emailAddress.$errors"
                    :key="error.$uid"
                >
                    <p>{{ error.$message }}</p>
                </div>
            </div>
            <div class="buttons-container">
                <text-button
                    type="submit"
                    btn-style="default-btn default-btn__medium default-btn__medium-width primary-btn align-item-center"
                    @run-method="continuePaymentJourney()"
                    data-cy="continue-payment-journey"
                    text-style="white-text"
                    text="labels.continueWithEmail"
                    :btn-loading="loadContinuePaymentJourney"
                    :disabled="loadContinuePaymentJourney"
                />
                <div class="or-separator">
                    <div></div>
                    <p>{{ t("default.or") }}</p>
                    <div></div>
                </div>

                <p
                    class="mt-4 underline-text pointer-cursor"
                    @click="handleLogout"
                    data-cy="logout-and-use-a-different-address"
                >
                    {{ t("labels.logoutAndUseADifferentEmail") }}
                </p>
            </div>
        </form>
    </div>
</template>

<script setup lang="ts">
import { reactive, computed, onMounted, ref, watch } from "vue";
import useVuelidate from "@vuelidate/core";
import { usePiniaStore } from "@/plugins/store";
import { useRouter, useRoute } from "vue-router";
import { useI18n } from "vue-i18n";

import textInput from "@/components/base/textInput.vue";
import textButton from "@/components/base/textButton.vue";

import { required, email } from "@/helpers/i18nValidation.ts";
import { getFormData } from "@/apis/esendaV2/organisations";
import { showAlert } from "@/helpers/errorHandling";
import { handleLogout } from "@/helpers/session";

import { configSentryError } from "@/helpers/sentry";

import { routeNames } from "@/constants/routeNames";

const { t } = useI18n();
const pStore = usePiniaStore();
const router = useRouter();
const route = useRoute();

const props = defineProps<{
    skipGetForm: boolean;
    redirectTo: routeNames;
}>();

const shortcode = computed(() => route.params.shortcode);
const formName = computed(() =>
    route.params.form ? route.params.form : "invoice",
);
const accountAlias = computed(() =>
    route.params.accountAlias ? String(route.params.accountAlias) : null,
);

/** Input values */
const emailForm = reactive({
    emailAddress: "",
});

/** Validation config */
const rules = computed(() => {
    return {
        emailAddress: { required, email, $autoDirty: true },
    };
});
const v$ = useVuelidate(rules, emailForm);

const loadContinuePaymentJourney = ref(false);

const continuePaymentJourney = async () => {
    /** Redirect the user to the correct route */
    loadContinuePaymentJourney.value = true;
    const routeName =
        props.redirectTo === routeNames.SUPPLEMENTARY_FORM
            ? routeNames.SUPPLEMENTARY_FORM
            : routeNames.E_MANDATES_FORM;

    const params = {
        shortcode: shortcode.value,
        ...(props.redirectTo === routeNames.SUPPLEMENTARY_FORM
            ? { form: formName.value }
            : { accountAlias: accountAlias.value }),
    };

    if (
        props.redirectTo === routeNames.E_MANDATES_FORM &&
        !accountAlias.value
    ) {
        loadContinuePaymentJourney.value = false;
        showAlert("alerts.incorrectEmandatesUrl", true);
        return;
    }

    router.push({ name: routeName, params }).catch((error) => {
        configSentryError(
            "authenticatedUserLogIn",
            "postLogInRedirect",
            `Error redirecting users after login: ${error}`,
        );
    });
};

const getForm = async () => {
    /** Exit get form if the prop is set to true, otherwise call the API */
    if (props.skipGetForm) {
        return;
    }

    await getFormData(String(shortcode.value), String(formName.value));
};

const storedEmailAddress = computed(() => pStore.getEmailAddress);
watch(
    storedEmailAddress,
    (newValue) => {
        /** Checks the stored email address for a new value to populate the field */
        if (newValue != null) {
            emailForm.emailAddress = newValue;
        }
    },
    { immediate: true },
);

onMounted(() => {
    /** Pre-fill email address input if it exits in the session storage */
    emailForm.emailAddress = pStore.getEmailAddress ?? "";

    // Set the origin route
    props.redirectTo === routeNames.E_MANDATES_FORM
        ? pStore.setOrigin(routeNames.E_MANDATES_LANDING_PAGE)
        : pStore.setOrigin(routeNames.LOGIN_PAGE);
});

getForm();
</script>
